/**
 * Drystone material palette.
 *
 * Authorised by: design-direction §2 (DECIDED: Drystone structure, Waymark discipline —
 * "slate, moss, wool, oak; matte, tactile, low-gloss") and §3 (one focus accent,
 * candidate space "gorse yellow, kite red").
 *
 * LAW (design-direction §3): red is never a state. There is no `danger` ramp in this
 * file and there must never be one. Hard deadlines get prominence (size, position,
 * persistence), never alarm colour.
 *
 * Each material is a 12-stop ramp from lightest (0) to darkest (11) so themes can be
 * derived by index rather than by hand-picked one-off hexes.
 */

export type Ramp = readonly [
  string, string, string, string, string, string,
  string, string, string, string, string, string,
];

/** Blue-grey. The structural material: surfaces, text, borders. */
export const slate: Ramp = [
  '#F7F8F9', '#EDEFF1', '#DDE1E5', '#C6CCD2', '#A8B1BA', '#8A95A0',
  '#6D7885', '#55606C', '#414A54', '#2F363E', '#20262C', '#14181C',
] as const;

/** Muted green. Growth, warmth-adjacent, the compost layer's living end. */
export const moss: Ramp = [
  '#F4F7F2', '#E7EEE3', '#D2DFCA', '#B4C9A8', '#93B083', '#759663',
  '#5C7C4C', '#48633C', '#384E2F', '#2A3B24', '#1D2A19', '#131C10',
] as const;

/** Pale grey-green. The quiet material: cooling loops, secondary surfaces. */
export const lichen: Ramp = [
  '#F6F7F4', '#EBEDE7', '#DADED3', '#C1C8B7', '#A4AE97', '#88937A',
  '#6E7962', '#57604D', '#434B3C', '#32372C', '#22261E', '#161913',
] as const;

/** Weathered warm brown. Tactile accents, capsule stone, cairn. */
export const oak: Ramp = [
  '#FAF6F2', '#F2EBE3', '#E4D8C9', '#D0BCA5', '#B79C7D', '#9C7E5D',
  '#82664A', '#69513A', '#52402E', '#3D2F23', '#2A2018', '#1B1510',
] as const;

/** Cream. The warm ground of the light theme. */
export const wool: Ramp = [
  '#FDFCFA', '#FAF8F4', '#F4F1EA', '#EAE5DA', '#DAD3C4', '#C5BCA9',
  '#ABA08B', '#8C8271', '#6E6659', '#514B42', '#37332C', '#22201B',
] as const;

/**
 * Gorse yellow — THE focus accent. DECIDED here from the design-direction §2
 * candidate space (gorse yellow / kite red). Chosen over kite red because
 * "red is never a state" (§3) makes a red accent a permanent ambiguity risk:
 * a user glancing at a red-accented warm loop cannot know it isn't an alarm.
 * Gorse is Welsh-landscape-native, bold enough for an app-store screenshot,
 * and semantically unclaimed.
 *
 * Deployed ONLY on the user's current warm focus (§2).
 */
export const gorse: Ramp = [
  '#FFFCF0', '#FFF6D6', '#FFEBAB', '#FCDA74', '#F5C43C', '#E5AE14',
  '#C79107', '#A2750A', '#7E5B0D', '#5D440F', '#3E2E0C', '#271D08',
] as const;

/**
 * Ember — survives from the killed "Hearth" candidate as the warmth of the
 * lowest-stimulation night theme only (design-direction §2: "Hearth survives
 * only as the top end of the darkness range").
 */
export const ember: Ramp = [
  '#FFF7F2', '#FFEADF', '#FFD3BC', '#F9B491', '#EE9166', '#DC6F40',
  '#BE5730', '#9B4527', '#7A3721', '#5B2A1B', '#3D1D14', '#26120C',
] as const;

export const palette = { slate, moss, lichen, oak, wool, gorse, ember } as const;
export type PaletteName = keyof typeof palette;
